import { api } from '@/v2/plugins/axios';
import { collectionGroup, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuthStore } from '@/v2/store/shared/auth-store';

const API_PATH = '/v1/whatsapp/conversation/labels';

// `array-contains-any` accepts at most 30 values, and each label costs one.
const MAX_LABELS_PER_QUERY = 30;

// A label filter that matches this many conversations is not a useful filter, and
// reading past it only burns Firestore quota.
const MAX_CONVERSATIONS_SCANNED = 5000;

class LabelsEntity {
	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
	}

	static fetch = async (queryParams) => {
		try {
			const { data: responseData } = await api.get(API_PATH, {
				params: queryParams,
			});
			const { result } = responseData;
			const { data } = result;

			return {
				data: data.map((obj) => {
					return new LabelsEntity(obj);
				}),
				totalRecords: result?.total,
			};
		} catch (e) {
			console.log(e);
		}
	};

	static create = async (data) => {
        return api.post(API_PATH, data);
	};

	static get = async (id) => {
        return api.get(`${API_PATH}/${id}`);
	};

	static update = async (id, data) => {
        return api.patch(`${API_PATH}/${id}`, data);
	};

	static remove = async (id) => {
        return api.delete(`${API_PATH}/${id}`);
	};

	/**
	 * Resolve labels to the contacts that carry them.
	 *
	 * Labels only exist in Firestore, on the conversation document, so there is no way
	 * to ask the API for "contacts tagged X" directly. We look the conversations up
	 * here and hand the resulting contacts to the API as a filter.
	 *
	 * A conversation identifies its contact by external id, not by the customers table
	 * primary key: the document is stored under `conversations/{external_id}` by both
	 * this app and the backend. So the document id is what the API can match on — the
	 * `customer_id` field holds the conversation id, which is a different thing.
	 *
	 * Conversations store a label as `{ id, color }` and Firestore can only match the
	 * whole object, so we rebuild it from the labels list the same way the chats page
	 * does — see stores/chats-list.js.
	 *
	 * @param {Array<string>} labelIds
	 * @param {Array<Object>} labels the label list from the label store
	 * @returns {Promise<Array<string>>} external ids of contacts carrying any label
	 */
	static fetchContactExternalIdsByLabels = async (labelIds, labels = []) => {
		if (!labelIds?.length) return [];

		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const labelObjects = labelIds.slice(0, MAX_LABELS_PER_QUERY).map((labelId) => {
			const label = labels.find((el) => el.id == labelId);

			return {
				id: labelId,
				color: label?.color_object?.hex_code?.primary,
			};
		});

		const snapshot = await getDocs(
			query(
				collectionGroup(db, 'conversations'),
				where('tenantId', '==', `${tenantId}`),
				where('labels', 'array-contains-any', labelObjects),
				limit(MAX_CONVERSATIONS_SCANNED),
			),
		);

		// One contact can hold several conversations (one per channel), all filed
		// under the same external id, so dedupe.
		const externalIds = new Set();
		snapshot.docs.forEach((doc) => {
			if (doc.id) externalIds.add(doc.id);
		});

		return [...externalIds];
	};

}

export default LabelsEntity;
